import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface PatientProjectData {
  patient?: unknown;
  project?: unknown;
  alias?: string | null;
}

interface PatientContextValue {
  patient: unknown;
  project: unknown;
  alias: string | null;
  setPatient: React.Dispatch<React.SetStateAction<unknown>>;
  setProject: React.Dispatch<React.SetStateAction<unknown>>;
  setAlias: React.Dispatch<React.SetStateAction<string | null>>;
  setData: (data?: PatientProjectData) => void;
}

const defaultContextValue: PatientContextValue = {
  patient: null,
  project: null,
  alias: null,
  setPatient: () => undefined,
  setProject: () => undefined,
  setAlias: () => undefined,
  setData: () => undefined,
};

export const PatientContext =
  createContext<PatientContextValue>(defaultContextValue);

interface PatientProviderProps {
  children: ReactNode;
}

export function PatientProvider({ children }: PatientProviderProps) {
  const [patient, setPatient] = useState<unknown>(null);
  const [project, setProject] = useState<unknown>(null);
  const [alias, setAlias] = useState<string | null>(null);

  const setData = useCallback((data?: PatientProjectData) => {
    setPatient(data?.patient ?? null);
    setProject(data?.project ?? null);
    setAlias(data?.alias ?? null);
  }, []);

  const value = useMemo(
    () => ({
      patient,
      project,
      alias,
      setPatient,
      setProject,
      setAlias,
      setData,
    }),
    [patient, project, alias, setData]
  );

  return (
    <PatientContext.Provider value={value}>
      {children}
    </PatientContext.Provider>
  );
}

export const usePatient = () => useContext(PatientContext);
